import { ApolloProvider } from '@apollo/client/react';
// AntD/MUI removed; use DS only
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { NotificationHost } from 'design-system';
import { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';
import subscribe from './actionCable/subscribe';
import { realtimeRuntime } from './actionCable/realtimeRuntime.js';
import { clearLegacyGoogleAnalytics } from './analytics/consent';
import client from './apolloClient';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import IntlBridge from './components/IntlBridge.jsx';
import { resetEnsureConversationReadyFlights } from './e2ee/mlsOrchestrator.js';
import { resetSessionManagerMLS } from './e2ee/sessionManagerMLS.js';
import { clearSessionManagerV2Cache } from './features/messenger-v2/runtime/sessionManagerV2.js';
import { RoomInboxProvider } from './features/rooms/RoomInboxContext.jsx';
import './global.css';
import { getEnv } from './lib/env';
import AppRouter from './pages/Router';
import { setTranslation } from './redux/actions';
import { CONSTANTS, LanguageKeys, getDefaultLanguage } from './service/constants';
import {
  AUTH_TOKEN_REFRESHED_EVENT,
  AUTH_TOKEN_REMOVED_EVENT,
  getToken,
  isJwtExpiringSoon,
  trySilentRefresh,
} from './service/request';
import './styles/settings.css';
import './styles/typography.css';
import messages from './translations';

const stripeKey = getEnv('REACT_APP_STRIPE_KEY');
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const buildStripeAppearance = () => {
  if (typeof document === 'undefined') {
    return { theme: 'night' };
  }
  const styles = getComputedStyle(document.documentElement);
  const text = styles.getPropertyValue('--text').trim() || '#f8fafc';
  const secondary = styles.getPropertyValue('--text-secondary').trim() || '#94a3b8';
  const border = styles.getPropertyValue('--border').trim() || '#334155';
  const background = styles.getPropertyValue('--input-bg').trim() || '#0f172a';
  return {
    theme: 'night',
    variables: {
      colorText: text,
      colorTextPlaceholder: secondary,
      colorPrimaryText: text,
      colorIcon: text,
      colorBackground: background,
      colorBorder: border,
    },
    rules: {
      '.Input': {
        color: text,
      },
      '.Input::placeholder': {
        color: secondary,
      },
      '.Label': {
        color: text,
      },
    },
  };
};

const maskUrlToken = (value) => {
  if (!value) return value;
  return value.replace(/(token=)([^&]+)/, (_, prefix, tokenValue) => {
    if (tokenValue.length <= 12) return `${prefix}${tokenValue}`;
    return `${prefix}${tokenValue.slice(0, 6)}…${tokenValue.slice(-6)}`;
  });
};

function App() {
  const translationKey = useSelector((state) => state.auth.translationKey);
  const user = useSelector((state) => state.user?.user);
  const isAuth = useSelector((state) => state.auth.isAuthorized);
  const dispatch = useDispatch();
  const [authToken, setAuthToken] = useState(() => getToken());

  const token = authToken;
  // Build a robust WS URL with ?token= via URL API to avoid malformed paths
  const url = (() => {
    if (!token || isJwtExpiringSoon(token, 15)) return null;
    const base = CONSTANTS.actionCable;
    try {
      const u = new URL(base);
      u.searchParams.set('token', token);
      return u.toString();
    } catch {
      const sep = String(base || '').includes('?') ? '&' : '?';
      return `${base}${sep}token=${encodeURIComponent(token)}`;
    }
  })();

  // Determine theme preference before rendering dark mode
  useEffect(() => {
    const html = document.documentElement;
    const userTheme = user?.preferred_theme || user?.theme;
    const getBaseTheme = () => localStorage.getItem('theme') || userTheme || 'light';
    const AUTO_KEY = 'theme_auto_override';
    let autoTimer;

    if (userTheme) {
      localStorage.setItem('theme', userTheme);
    }

    const resolveAutoTheme = () => {
      const hour = new Date().getHours();
      return hour >= 18 || hour < 7 ? 'dark' : 'light';
    };

    let isApplying = false;
    const applyTheme = (theme) => {
      isApplying = true;
      html.setAttribute('data-theme', theme);
      window.requestAnimationFrame(() => {
        isApplying = false;
      });
    };

    const setAutoState = (state) => {
      if (state) {
        localStorage.setItem(AUTO_KEY, state);
      } else {
        localStorage.removeItem(AUTO_KEY);
      }
    };

    const shouldAutoApply = () => localStorage.getItem(AUTO_KEY) !== 'manual';

    const scheduleNextToggle = () => {
      const now = new Date();
      const hour = now.getHours();
      const next = new Date(now);
      if (hour >= 18) {
        next.setDate(now.getDate() + 1);
        next.setHours(7, 0, 0, 0);
      } else if (hour < 7) {
        next.setHours(7, 0, 0, 0);
      } else {
        next.setHours(18, 0, 0, 0);
      }
      const delay = Math.max(0, next.getTime() - now.getTime());
      autoTimer = window.setTimeout(() => {
        if (resolveAutoTheme() === 'dark') {
          if (shouldAutoApply()) {
            applyTheme('dark');
            setAutoState('auto');
          }
        } else {
          const currentAuto = localStorage.getItem(AUTO_KEY);
          if (currentAuto === 'auto') {
            applyTheme(getBaseTheme());
          }
          setAutoState(null);
        }
        scheduleNextToggle();
      }, delay);
    };

    if (resolveAutoTheme() === 'dark') {
      if (shouldAutoApply()) {
        applyTheme('dark');
        setAutoState('auto');
      } else {
        applyTheme(getBaseTheme());
      }
    } else {
      applyTheme(getBaseTheme());
      setAutoState(null);
    }
    scheduleNextToggle();

    const observer = new MutationObserver(() => {
      if (isApplying) return;
      if (resolveAutoTheme() !== 'dark') return;
      const current = html.getAttribute('data-theme');
      if (current !== 'dark' && localStorage.getItem(AUTO_KEY) === 'auto') {
        setAutoState('manual');
      }
    });
    observer.observe(html, { attributes: true, attributeFilter: ['data-theme'] });

    return () => {
      if (autoTimer) {
        clearTimeout(autoTimer);
      }
      observer.disconnect();
    };
  }, [user]);

  useEffect(() => {
    if (user?.preferred_language?.key) {
      const { key } = user.preferred_language;
      const available = Object.values(LanguageKeys);
      const language = available.includes(key) ? key : LanguageKeys.Norwegian;
      dispatch(setTranslation(language));
    } else {
      dispatch(setTranslation(getDefaultLanguage()));
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (!isAuth) {
      setAuthToken(null);
      return undefined;
    }

    let disposed = false;
    const syncTokenState = (nextToken = getToken()) => {
      if (!disposed) setAuthToken(nextToken || null);
    };

    const handleTokenRefreshed = (event) => {
      syncTokenState(event?.detail?.token || getToken());
    };
    const handleTokenRemoved = () => {
      syncTokenState(null);
    };
    const handleStorage = (event) => {
      if (event.key === 'token' || event.key === 'ng.auth.token_sync') {
        syncTokenState();
      }
    };
    window.addEventListener(AUTH_TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
    window.addEventListener(AUTH_TOKEN_REMOVED_EVENT, handleTokenRemoved);
    window.addEventListener('storage', handleStorage);
    syncTokenState();

    return () => {
      disposed = true;
      window.removeEventListener(AUTH_TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
      window.removeEventListener(AUTH_TOKEN_REMOVED_EVENT, handleTokenRemoved);
      window.removeEventListener('storage', handleStorage);
    };
  }, [isAuth]);

  useEffect(() => {
    if (!isAuth || typeof window === 'undefined') return undefined;

    const currentToken = getToken();
    if (currentToken && !isJwtExpiringSoon(currentToken, 180)) return undefined;

    let cancelled = false;
    void Promise.resolve(trySilentRefresh())
      .then((refreshed) => {
        if (cancelled) return;
        const nextToken = getToken();
        setAuthToken(refreshed && nextToken ? nextToken : null);
      })
      .catch(() => {
        if (!cancelled) setAuthToken(null);
      });

    return () => {
      cancelled = true;
    };
  }, [authToken, isAuth]);

  useEffect(() => {
    const maskedUrl = url ? maskUrlToken(url) : null;
    const accountId = user?.id;
    if (!isAuth || !url || !accountId) {
      realtimeRuntime.destroy();
      return;
    }

    try {
      const consumer = realtimeRuntime.configure({ url, accountId });
      if (typeof window !== 'undefined') {
        window.__cable = consumer;
        window.__nettgravCable = consumer;
      }
      console.info('[ActionCable] runtime configured for', maskedUrl);
      subscribe({ id: accountId }, consumer);
    } catch (error) {
      console.error('[ActionCable] failed to configure realtime runtime', error);
    }
  }, [user?.id, isAuth, url]);

  useEffect(() => {
    if (isAuth) return;

    resetEnsureConversationReadyFlights();
    clearSessionManagerV2Cache();
    (async () => {
      try {
        await resetSessionManagerMLS({ teardown: true });
      } catch (error) {
        console.warn('[MLS] Failed to reset MLS manager on logout', error);
      }
    })();
  }, [isAuth]);

  useEffect(
    () => () => {
      realtimeRuntime.destroy();
    },
    [],
  );

  useEffect(() => {
    clearLegacyGoogleAnalytics();
  }, []);

  // MLS bootstrap is conversation-scoped in useConversationE2EE.
  // Keep app-level startup free of runtime warmup to avoid duplicate
  // rehydrate races on reload/login.

  // Track theme via <html data-theme>
  const [isDark, setIsDark] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') === 'dark';
    }
    return false;
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    const update = () => setIsDark(html.getAttribute('data-theme') === 'dark');
    update();
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'data-theme') {
          update();
        }
      }
    });
    observer.observe(html, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const [stripeAppearance, setStripeAppearance] = useState(() => buildStripeAppearance());

  useEffect(() => {
    setStripeAppearance(buildStripeAppearance());
  }, [isDark]);

  return (
    <IntlProvider locale={translationKey} messages={{ ...messages.ENG, ...messages[translationKey] }}>
      <ApolloProvider client={client}>
        <Elements stripe={stripePromise} options={{ appearance: stripeAppearance }}>
          <ErrorBoundary>
            <div className="App">
              <IntlBridge />
              <RoomInboxProvider>
                <AppRouter />
              </RoomInboxProvider>
              <NotificationHost />
            </div>
          </ErrorBoundary>
        </Elements>
      </ApolloProvider>
    </IntlProvider>
  );
}

export default App;
